import './index.less';
import { useEffect, useState, useRef, useContext } from 'react';
import { ReactComponent as CloseSvg } from '@/commons/icons/panel-icon/close.svg';
import { Select, message } from 'antd';
import { StoreyContext } from '@/App';
import { StoreyStatus } from '@/commons/context/storey-store';
import { ComponentTypeList } from '@/commons/enums/component-type';
import { Storey } from '@/commons/interface/building/storey';
import { graphicStore } from '@/commons/store/graphic-store';
import { DefaultStoreyUuid, GroupType } from 'tnbimbase';
import { BlockT, ModelBase } from 'pytha';
import { Vector3 } from 'three';
import { createPrimitiveMulti } from '@/api/geometry/primitive';
import { useParams } from 'react-router-dom';

interface IProps {
    onClose: () => void
}

export default function StoreyCopyPanel(props: IProps) {
    const { projectUuid, modelUuid } = useParams();
    const storeyContext = useContext<StoreyStatus>(StoreyContext);
    const [sourceStoreyUuid, setSourceStoreyUuid] = useState<string>('');
    const [targetStoreyUuid, setTargetStoreyUuid] = useState<string>('');

    useEffect(() => {
        if (storeyContext?.currentStorey) {
            setSourceStoreyUuid(storeyContext?.currentStorey?.uuid);
        }
    }, [])

    const onCancel = () => {
        props.onClose();
    }

    const onSave = () => {
        if (!graphicStore?.extraContext?.getCurrentViewEditor()) return;
        if (!sourceStoreyUuid) {
            message.warning("未选择拷贝楼层");
            return;
        }
        if (!targetStoreyUuid) {
            message.warning("未选择目标楼层");
            return;
        }
        if (sourceStoreyUuid === targetStoreyUuid) {
            message.warning("拷贝楼层和目标楼层不能相同");
            return;
        }
        let editor = graphicStore?.extraContext?.getCurrentViewEditor();
        // TODO 复制一份 然后编组
        let storeyList: Storey[] = storeyContext.storeys?.sort((a, b) => a.bottomElevation - b.bottomElevation) || [];
        if (storeyList?.length === 0) {
            message.error("楼层不存在");
            props.onClose();
            return;
        }
        let entitiesInSourceStorey: ModelBase[] = [];
        Object.keys(editor.entityDict).forEach(uuid => {
            let entity = editor.entityDict[uuid];
            if (!entity) return;

            if (entity.group && entity.group[GroupType.STOREY] && entity.group[GroupType.STOREY].length > 0) {
                // 如果该图有编组属性，并且楼层编组属性存在，并且楼层编组数组的长度大于0
                if (entity.group[GroupType.STOREY]?.includes(sourceStoreyUuid)) {
                    // 如果图元的楼层编组uuid为当前uuid，则加入到数组中
                    entitiesInSourceStorey.push(entity);

                } else if (entity.group[GroupType.STOREY]?.includes(DefaultStoreyUuid.DYNAMIC)) {
                    // 如果图元的楼层编组为动态指定，则计算其层高，将层高符合条件的图元全部加入
                    if (entity.baseType === 'BlockT') {
                        // 如果图元为块，直接用position判断高度，减少对于中心点的计算
                        let block = entity as BlockT;
                        //毫米与米换算
                        const z = block.position.z / 1000;
                        const index = storeyList?.findIndex(item => item.uuid === sourceStoreyUuid);
                        if (index === 0) {
                            if (z >= storeyList[index].bottomElevation) {
                                // _selectEntity(editor, entitiesInCurrentStorey, entity);
                                entitiesInSourceStorey.push(entity);
                            }
                        } else if (index === storeyList.length - 1) {
                            if (z < storeyList[index - 1].bottomElevation) {
                                // _selectEntity(editor, entitiesInCurrentStorey, entity);
                                entitiesInSourceStorey.push(entity);
                            }
                        } else {
                            if (z < storeyList[index - 1].bottomElevation && z >= storeyList[index].bottomElevation) {
                                // _selectEntity(editor, entitiesInCurrentStorey, entity);
                                entitiesInSourceStorey.push(entity);
                            }
                        }
                    } else {
                        let vec = new Vector3();
                        entity.getCenter(vec)
                        //毫米与米换算
                        const z = vec.z / 1000;
                        const index = storeyList?.findIndex(item => item.uuid === sourceStoreyUuid);
                        if (index === 0) {
                            if (z >= storeyList[index].bottomElevation) {
                                // _selectEntity(editor, entitiesInCurrentStorey, entity);
                                entitiesInSourceStorey.push(entity);
                            }
                        } else if (index === storeyList.length - 1) {
                            if (z < storeyList[index - 1].bottomElevation) {
                                // _selectEntity(editor, entitiesInCurrentStorey, entity);
                                entitiesInSourceStorey.push(entity);
                            }
                        } else {
                            if (z < storeyList[index - 1].bottomElevation && z >= storeyList[index].bottomElevation) {
                                // _selectEntity(editor, entitiesInCurrentStorey, entity);
                                entitiesInSourceStorey.push(entity);
                            }
                        }
                    }
                }
            }
        });
        if (entitiesInSourceStorey?.length === 0) {
            message.info("拷贝楼层没有编组图元");
            props.onClose();
            return;
        }
        let sourceStorey = storeyList?.find(item => item.uuid === sourceStoreyUuid);
        let targetStorey = storeyList?.find(item => item.uuid === targetStoreyUuid);
        let dir = new Vector3(0, 0, 1);
        let dict = (targetStorey.bottomElevation - sourceStorey.bottomElevation) * 1000;
        let targetEntities = [];
        for (let item of entitiesInSourceStorey) {
            if (!item?.uuid) continue;
            let entity = editor.entityDict[item.uuid];
            if (!entity) continue;
            let clone = entity.clone();
            clone.move(dir, dict);
            if (!clone?.group) {
                clone.group = {};
            }
            let oldStoreyArr = clone.group[GroupType.STOREY]?.filter(item => item === DefaultStoreyUuid.DYNAMIC) || [];
            clone.group[GroupType.STOREY] = [...oldStoreyArr, targetStoreyUuid];
            targetEntities.push(clone);
        }
        if (targetEntities?.length === 0) {
            props.onClose();
            return;
        }

        createPrimitiveMulti(targetEntities?.map(item => item.toJson()), {
            modelUuid: modelUuid,
            projectUuid: projectUuid,
        }).then(res => {
            if (!storeyContext?.currentStorey || storeyContext.currentStorey?.uuid === targetStoreyUuid) {
                editor.addObjects(targetEntities);
            }
            message.success("拷贝成功");
            props.onClose();
        }).catch(err => {
            message.error("拷贝失败");
        })
    }

    return (
        <div className='storey-copy-container'>
            <div className="top-panel">
                <div className="text">拷贝楼层图元</div>
                <div className="cancel-icon" onClick={onCancel}><CloseSvg /></div>
            </div>
            <div className='content-panel'>
                <div className='content-row'>
                    拷贝
                    <Select
                        popupClassName='side-title-pop'
                        value={sourceStoreyUuid}
                        onChange={(e) => setSourceStoreyUuid(e)}
                    >
                        {storeyContext?.storeys?.map((item, index) => (
                            <Select.Option value={item.uuid} key={index}>
                                {item?.name || ''}
                            </Select.Option>
                        ))}
                    </Select>
                    所有图元至
                    <Select
                        popupClassName='side-title-pop'
                        value={targetStoreyUuid}
                        onChange={(e) => setTargetStoreyUuid(e)}
                    >
                        {storeyContext?.storeys?.map((item, index) => (
                            <Select.Option value={item.uuid} key={index}>
                                {item?.name || ''}
                            </Select.Option>
                        ))}
                    </Select>
                </div>
            </div>
            <div className="bottom-panel">
                <div className="btn save" onClick={onSave}>确定</div>
                <div className="btn cancel" onClick={onCancel}>取消</div>
            </div>
        </div>
    )
}